import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, SubmitHandler } from "react-hook-form";
import dayjs from "dayjs";

import { hideModal, hideChildModal, showChildModal } from "../../data/modal";
import { AutocompleteField } from "../FormFields/RenderAutocompleteField";
import { useRefreshVoyageData } from "../hooks/useRefreshVoyageData";
import { selectedFilteredVoyage } from "../../selectors/filterSelector";
import { NumberField } from "../FormFields/RenderNumberField";
import ProgressOverlay from "../partial/ProgressionOverlay";
import { useTranslation } from 'react-i18next';

import {
  Box,
  Container,
  Grid,
  Button,
  LinearProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  AddLink as AddLinkIcon,
} from "@mui/icons-material";
import { CheckboxField } from "../FormFields/RenderCheckboxField";

interface Props {
  terminalCalls: any[];
  selectedVessel: any;
  selectedLoadingTerminalCall: any;
  containerTypes: any[];
}
interface FormValues {
  containers: number;
  weight: number;
  containerType: any;
  loadingTerminalCall: any;
  unloadingTerminalCall: any;
}

const SimpleMultipleContainerForm: React.FC<Props> = ({
  terminalCalls,
  selectedVessel,
  selectedLoadingTerminalCall,
  containerTypes,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { voyage } = useSelector(selectedFilteredVoyage);
  const { modal } = useSelector((s: any) => ({ modal: s.modal }));
  const refreshVoyageData = useRefreshVoyageData();

  const openChildModal = useCallback(
    (opts: any) => dispatch(showChildModal(opts)),
    [dispatch]
  );
  const close = () =>
    modal.childModal ? dispatch(hideChildModal()) : dispatch(hideModal());

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      loadingTerminalCall: selectedLoadingTerminalCall?.['@id'],
      vessel: selectedVessel?.['@id'],
      voyage: voyage?.['@id'],
    },
    shouldUnregister: false,
  });

  const terminalCallOptions = React.useMemo(
    () =>
      terminalCalls.map((tc) => ({
        '@id': tc["@id"],
        name: tc.planned && tc.terminal
          ? `${dayjs(tc.planned).format("DD/MM/YYYY HH:mm")} – ${tc.terminal.name}`
          : tc.terminal?.name || tc["@id"],
        raw: tc,
      })),
    [terminalCalls]
  );

  const containerTypeOptions = React.useMemo(
    () =>
      containerTypes.map((ct) => ({
        '@id': ct["@id"],
        name: ct.name,
        raw: ct,
      })),
    [containerTypes]
  );

  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    try {
      await fetch("/api/multiple-container/creation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(formData),
      });
    } catch (e) {
      console.error("Save failed", e);
    } finally {
      close();
      refreshVoyageData(voyage);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} p={2} position="relative">
      <ProgressOverlay active={isSubmitting} />
      <Container sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2}>
            <NumberField
              name="containers"
              label={`# ${t("containers")}`}
              control={control}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <NumberField
              name="weight"
              label={t("weightInKilo")}
              control={control}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutocompleteField
              name="containerType"
              label={t("containerType")}
              control={control}
              options={containerTypeOptions}
              required
              onCreateClick={() =>
                openChildModal({
                  title: t("addFormTitleByType", {type: t("containerType")}),
                  form: "containerType",
                  formProps: { callback: setValue },
                })
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AutocompleteField
              name="loadingTerminalCall"
              label={t("departure")}
              control={control}
              options={terminalCallOptions}
              required
              onCreateClick={() =>
                openChildModal({
                  title: `Add planning to ${selectedLoadingTerminalCall?.terminal?.name}`,
                  form: "simpleterminalcall",
                  formProps: {
                    callback: setValue,
                    terminal: selectedLoadingTerminalCall?.terminal,
                    vessel: selectedVessel,
                  },
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutocompleteField
              name="unloadingTerminalCall"
              label={t("destination")}
              control={control}
              options={terminalCallOptions}
              required
              onCreateClick={() =>
                openChildModal({
                  title: `Add planning to ${selectedLoadingTerminalCall?.terminal?.name}`,
                  form: "simpleterminalcall",
                  formProps: {
                    callback: setValue,
                    vessel: selectedVessel,
                    showTerminalField: true,
                  },
                })
              }
            />
          </Grid>
        </Grid>
      </Container>
      <Grid container justifyContent="space-between" mt={2}>
        <Button variant="outlined" onClick={close} startIcon={<CloseIcon />}>
          {t("cancel")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<AddLinkIcon />}
          disabled={isSubmitting}
        >
          {t("createContainers")}
        </Button>
      </Grid>
    </Box>
  );
};

export default SimpleMultipleContainerForm;